import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { auth } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/ui/card";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../components/ui/tabs";

import Loader from "../../components/Loader";
import { useToast } from "../../components/hooks/use-toast";

import UsersList from "./UsersList";

const UserManagement = () => {
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [activeTab, setActiveTab] = useState("admins");

  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const checkUserRole = async (user) => {
      try {
        // Get the ID token and decode the custom claims
        const idTokenResult = await user.getIdTokenResult();

        // Check if the user has the 'admin' role
        if (idTokenResult.claims.role === "admin") {
          setIsAuthorized(true);
        } else {
          setIsAuthorized(false);

          toast({
            variant: "destructive",
            title: "Permission denied.",
            description: "You are not authorized to access this page.",
          });

          navigate("/"); // Redirect if not authorized
        }
      } catch (error) {
        console.error("Error checking role:", error);
        setIsAuthorized(false);
        navigate("/"); // Redirect if not authorized
      } finally {
        setLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Force token refresh to retrieve the new custom claims if any
        await user.getIdToken(true);

        checkUserRole(user);
      } else {
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, [toast, navigate]);

  if (loading) {
    return <Loader text={"Loading Admin Portal..."}></Loader>;
  }

  if (!isAuthorized) {
    return <div>You are not authorized to access this page.</div>;
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>User Management</CardTitle>
      </CardHeader>

      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 w-full flex justify-center">
            <TabsTrigger value="admins" className="flex-grow text-center">
              Admins
            </TabsTrigger>

            <TabsTrigger value="managers" className="flex-grow text-center">
              Managers
            </TabsTrigger>

            <TabsTrigger value="users" className="flex-grow text-center">
              Users
            </TabsTrigger>
          </TabsList>

          <TabsContent value="admins">
            <UsersList role="admin" />
          </TabsContent>

          <TabsContent value="managers">
            <UsersList role="manager" />
          </TabsContent>

          <TabsContent value="users">
            <UsersList role="user" />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default UserManagement;
